import React, { useState, useEffect } from "react";
import { Box, Typography, useTheme, Grid, FormControl, InputLabel, Select, MenuItem, OutlinedInput, Chip, IconButton } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { tokens } from "../../theme";
import axios from "axios";
import { API_URL } from "../../api-config";
import MatrixHeatmap from "./MatrixHeatmap";

const MarkovCalculator = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // Raw Data
    const [allComponents, setAllComponents] = useState([]);

    // Selections
    const [selectedModule, setSelectedModule] = useState("M1");
    const [selectedFilters, setSelectedFilters] = useState([]);
    const [calculatedMatrix, setCalculatedMatrix] = useState(null);
    const [availableFilters, setAvailableFilters] = useState([]);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(`${API_URL}/trading-api/toolbox/markov_components/`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = response.data;
            if (!Array.isArray(data)) {
                console.error("API response is not an array:", data);
                setAllComponents([]);
                return;
            }
            setAllComponents(data);

            // Extract unique modules that have a GLOBAL matrix
            const globalModules = [...new Set(data.filter(c => c.scope === 'GLOBAL').map(c => c.modulo))];
            if (globalModules.length > 0 && !globalModules.includes(selectedModule)) {
                setSelectedModule(globalModules[0]);
            }

            // Extract available filters (deltas)
            const deltas = data.filter(c => c.scope === 'DELTA');
            const filters = deltas.map(d => ({
                id: d.id,
                label: `${d.filter_type}: ${d.filter_value} (${d.modulo})`,
                type: d.filter_type,
                value: d.filter_value,
                modulo: d.modulo,
                obj: d
            }));
            setAvailableFilters(filters);

        } catch (error) {
            console.error("Error fetching components:", error);
        }
    };


    // Helper to convert object-of-objects to {index, columns, data} format
    const parseMatrixObject = (matrixObj) => {
        if (!matrixObj) return null;

        // Attempt to parse if string
        let raw = matrixObj;
        if (typeof raw === 'string') {
            try {
                raw = JSON.parse(raw);
            } catch (e) {
                console.error("Failed to parse matrix JSON string", e);
                return null;
            }
        }

        // Check if it's already in the {index, columns, data} format (just in case)
        if (raw.index && raw.columns && raw.data && Array.isArray(raw.data)) {
            return raw;
        }

        // Assume it's an Object of Objects: { "RowName": { "ColName": val, ... }, ... }
        // We need a stable order. Let's extract keys.
        const rows = Object.keys(raw);
        if (rows.length === 0) return null;

        // Assume all rows have same columns, take first row's keys
        const firstRow = raw[rows[0]];
        const cols = Object.keys(firstRow);

        // Define a standard order if possible, or just use sort
        // The user's example has specific order: Lower_P90 -> Upper_P90.
        // Let's rely on the order provided by Object.keys if the backend preserved it (Python 3.7+ dicts are ordered)
        // OR define a specific order for these known labels.
        const order = ["Upper_P90", "Upper_P75", "Upper_P50", "VWAP", "Lower_P50", "Lower_P75", "Lower_P90"];

        // Sort rows and cols based on standard order if they encompass these keys
        const sortedRows = rows.sort((a, b) => order.indexOf(a) - order.indexOf(b));
        const sortedCols = cols.sort((a, b) => order.indexOf(a) - order.indexOf(b));

        const data = sortedRows.map(r => {
            return sortedCols.map(c => {
                return raw[r][c] !== undefined ? raw[r][c] : 0;
            });
        });

        return {
            index: sortedRows,
            columns: sortedCols,
            data: data
        };
    };

    // Calculation Logic
    useEffect(() => {
        if (allComponents.length === 0) return;

        // 1. Find Base Matrix (GLOBAL) for selected Module
        const baseComponent = allComponents.find(c => c.scope === 'GLOBAL' && c.modulo === selectedModule);

        // Parse base matrix
        let baseParsed = null;
        if (baseComponent && baseComponent.matrix_json) {
            baseParsed = parseMatrixObject(baseComponent.matrix_json);
        }

        if (!baseParsed) {
            setCalculatedMatrix(null);
            return;
        }

        // Clone for calculations
        let resultData = baseParsed.data.map(row => [...row]);
        const columns = baseParsed.columns;
        const index = baseParsed.index;

        // Initialize breakdown structure (same dim as resultData)
        let breakdown = resultData.map(row => row.map(() => []));

        // 2. Add Deltas
        selectedFilters.forEach(filterId => {
            const deltaObj = allComponents.find(c => c.id === filterId);
            if (deltaObj && deltaObj.matrix_json) {
                const deltaParsed = parseMatrixObject(deltaObj.matrix_json);
                if (deltaParsed) {
                    // Add element-wise
                    for (let i = 0; i < resultData.length; i++) {
                        for (let j = 0; j < resultData[i].length; j++) {
                            // Safe add
                            if (deltaParsed.data[i] && deltaParsed.data[i][j] !== undefined) {
                                const val = deltaParsed.data[i][j];
                                resultData[i][j] += val;

                                // Store contribution for tooltip
                                if (Math.abs(val) > 0.000001) {
                                    breakdown[i][j].push({
                                        label: `${deltaObj.filter_type}: ${deltaObj.filter_value}`,
                                        value: val
                                    });
                                }
                            }
                        }
                    }
                }
            }
        });

        // 3. Stabilization / Normalization (L1)
        const stabilizedData = resultData.map(row => {
            // Clip negatives
            let clippedRow = row.map(v => Math.max(0, v));
            let sum = clippedRow.reduce((a, b) => a + b, 0);

            // Normalize
            if (sum > 0) {
                return clippedRow.map(v => v / sum);
            } else {
                return clippedRow.map(() => 0);
            }
        });

        // 4. Calculate Diff from Base (Net Impact of Filters + Normalization)
        let diffData = null;
        if (baseParsed && baseParsed.data) {
            diffData = stabilizedData.map((row, i) => {
                return row.map((val, j) => {
                    // baseParsed.data[i][j] should exist if dimensions match
                    const baseVal = baseParsed.data[i] && baseParsed.data[i][j] !== undefined ? baseParsed.data[i][j] : 0;
                    return val - baseVal;
                });
            });
        }

        setCalculatedMatrix({
            index: index,
            columns: columns,
            data: stabilizedData,
            diffs: diffData,
            breakdown: breakdown
        });

    }, [selectedModule, selectedFilters, allComponents]);


    // Handlers
    const handleModuleChange = (event) => {
        setSelectedModule(event.target.value);
        // Optional: clear filters if they are module-specific? 
        // User's Deltas seem to have 'modulo' field too. 
        // "Los Deltas Modulares... se calculan restando la matriz base... en ese subconjunto"
        // If deltas are specific to a module, we should filter the available deltas list.
    };

    const handleFilterChange = (event) => {
        const {
            target: { value },
        } = event;
        setSelectedFilters(
            // On autofill we get a stringified value.
            typeof value === 'string' ? value.split(',') : value,
        );
    };

    // Filter available options based on selected module?
    // "M_final = M_base + ... + Delta_Martes"
    // Does Delta_Martes exist for M1, M2, M3 separately? 
    // The model implies deltas might be specific or general. 
    // The DB has a 'modulo' column for everything.
    // So we should probably only allow selecting Deltas that match the current Module (or have modulo='ALL' if that exists).

    const currentModuleFilters = availableFilters.filter(f => f.modulo === selectedModule || f.modulo === 'ALL');

    return (
        <Box m="20px">
            <Box mb="30px">
                <Typography variant="h2" color={colors.grey[100]} fontWeight="bold" sx={{ m: "0 0 5px 0" }}>
                    MARKOV SIMULATOR
                </Typography>
                <Typography variant="h5" color={colors.greenAccent[400]}>
                    Interactive Transition Matrix Builder
                </Typography>
            </Box>

            <Grid container spacing={4}>
                {/* Controls Area */}
                <Grid item xs={12} md={4}>
                    <Box backgroundColor={colors.primary[400]} p="30px" borderRadius="8px">
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb="20px">
                            <Typography variant="h5" fontWeight="600" color={colors.grey[100]}>
                                Configuration
                            </Typography>
                            {selectedFilters.length > 0 && (
                                <IconButton
                                    onClick={() => setSelectedFilters([])}
                                    sx={{ color: colors.redAccent[500] }}
                                    title="Clear all filters"
                                >
                                    <CloseIcon />
                                </IconButton>
                            )}
                        </Box>

                        {/* Module Selection */}
                        <FormControl fullWidth sx={{ mb: 3 }}>
                            <InputLabel id="module-select-label">Base Module</InputLabel>
                            <Select
                                labelId="module-select-label"
                                value={selectedModule}
                                label="Base Module"
                                onChange={handleModuleChange}
                            >
                                <MenuItem value="M1">M1 (Morning)</MenuItem>
                                <MenuItem value="M2">M2 (Mid-Day)</MenuItem>
                                <MenuItem value="M3">M3 (Closing)</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Filters Selection */}
                        <FormControl fullWidth sx={{ mb: 3 }}>
                            <InputLabel id="filter-select-label">Apply Deltas</InputLabel>
                            <Select
                                labelId="filter-select-label"
                                multiple
                                value={selectedFilters}
                                onChange={handleFilterChange}
                                input={<OutlinedInput id="select-multiple-chip" label="Apply Deltas" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => {
                                            const filter = availableFilters.find(f => f.id === value);
                                            return (
                                                <Chip
                                                    key={value}
                                                    label={filter ? filter.label : value}
                                                    style={{ backgroundColor: colors.blueAccent[700], color: 'white' }}
                                                />
                                            );
                                        })}
                                    </Box>
                                )}
                            >
                                {currentModuleFilters.map((filter) => (
                                    <MenuItem
                                        key={filter.id}
                                        value={filter.id}
                                        style={{
                                            fontWeight: selectedFilters.indexOf(filter.id) === -1
                                                ? theme.typography.fontWeightRegular
                                                : theme.typography.fontWeightMedium,
                                        }}
                                    >
                                        {filter.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Typography variant="body2" color={colors.grey[300]} mt={2}>
                            Formula: <br />
                            <code>M_Final = M_{selectedModule} + Σ(Deltas)</code>
                        </Typography>
                        <Typography variant="caption" color={colors.grey[400]}>
                            *Values are automatically normalized (L1) to ensure valid probabilities.
                        </Typography>
                    </Box>
                </Grid>

                {/* Visualization Area */}
                <Grid item xs={12} md={8}>
                    <Box backgroundColor={colors.primary[400]} p="30px" borderRadius="8px" minHeight="500px" display="flex" flexDirection="column">
                        {allComponents.length === 0 ? (
                            <Box display="flex" justifyContent="center" alignItems="center" flexGrow={1}>
                                <Typography variant="h5" color={colors.grey[300]}>Fetching Components...</Typography>
                            </Box>
                        ) : (
                            <MatrixHeatmap
                                data={calculatedMatrix}
                                title={selectedFilters.length > 0
                                    ? `Projected Markov Matrix (${selectedModule} + ${selectedFilters.length} Deltas)`
                                    : `Base Markov Matrix (${selectedModule} - GLOBAL)`
                                }
                            />
                        )}
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default MarkovCalculator;
